import { useEffect, useState, useCallback } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";

const KIND_STYLE = {
  request: "border-orange-500/40 text-orange-700 bg-orange-500/5",
  approval: "border-emerald-500/40 text-emerald-700 bg-emerald-500/5",
  revision: "border-rose-500/40 text-rose-700 bg-rose-500/5",
  "social-connected": "border-emerald-500/40 text-emerald-700 bg-emerald-500/5",
  "project-assigned": "border-orange-500/40 text-orange-700 bg-orange-500/5",
  "team-removed": "border-neutral-400/40 text-neutral-600 bg-neutral-500/5",
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter(n => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative w-9 h-9 rounded-full grid place-items-center hover:bg-black/[0.04] border border-transparent hover:border-[#E4E7EC] transition-colors" data-testid="notification-bell">
          <Bell className="w-4 h-4 text-neutral-700"/>
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-neutral-900 text-[10px] font-mono font-bold grid place-items-center border border-[#FFFFFF]" data-testid="notification-unread-count">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 bg-[#FFFFFF] border-[#E4E7EC] p-0" data-testid="notification-popover">
        <div className="px-4 py-3 border-b border-[#E4E7EC] flex items-center justify-between">
          <div>
            <div className="text-sm font-bold">Notifications</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{unread} unread</div>
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAll} data-testid="notification-mark-all" className="text-[11px] font-mono uppercase">
              <CheckCheck className="w-3 h-3 mr-1"/> Read all
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-[#E4E7EC]">
          {items.length === 0 && <div className="p-6 text-sm text-neutral-500 text-center">No notifications yet</div>}
          {items.map(n => (
            <button key={n.id} onClick={()=>{ markOne(n.id); if ((n.kind?.startsWith("task-") || n.kind?.startsWith("bug-") || n.kind?.startsWith("standup-") || n.kind?.startsWith("project-")) && n.project_id) { setOpen(false); navigate(`/projects/${n.project_id}${n.kind.startsWith("bug-") ? "?tab=bugs" : n.kind.startsWith("standup-") ? "?tab=standup" : ""}`); } else if (n.kind === "social-connected" && n.client_id) { setOpen(false); navigate(`/clients/${n.client_id}/connections`); } }} className={`w-full text-left p-3 hover:bg-white/[0.03] transition-colors ${n.read?"opacity-60":""}`} data-testid={`notif-${n.id}`}>
              <div className="flex items-start gap-2">
                <span className={`pill ${KIND_STYLE[n.kind] || "pill-draft"} shrink-0`}>{n.kind}</span>
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 shrink-0"/>}
              </div>
              <div className="text-sm text-neutral-800 mt-1.5">{n.message}</div>
              <div className="text-[10px] font-mono text-neutral-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
